import { gql, useMutation } from '@apollo/client'
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  DialogActions,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  FormHelperText,
  Link,
} from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { RadioGroup, TextField, SimpleFileUpload } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import AppConfig from '@src/config/AppConfig'

const csvValidator = (csvData: string) => {
  const [header, ...rows] = csvData
    .trim()
    .split('\n')
    .map((row, index) =>
      index === 0
        ? row.toLowerCase().replace('\r', '').split(',')
        : row.split(','),
    )
  if (!header?.includes('email')) {
    return { message: I18n.t('leads.csvValidationEmail'), fieldName: 'import' }
  }
  if (!rows.length) {
    return { message: I18n.t('leads.csvValidationLength'), fieldName: 'import' }
  }
  return true
}

const CREATE_LEAD_IMPORT = gql`
  mutation CreateLeadImport(
    $resource_id: ID!
    $resource_type: String!
    $leadImport: LeadImportInput
  ) {
    createLeadImport(
      resource_id: $resource_id
      resource_type: $resource_type
      lead_import: $leadImport
    ) {
      id
    }
  }
`

const schema = Yup.object()
  .shape({
    brand_id: Yup.string()
      .required()
      .label(I18n.t('leads.importDialog.brandListLabel')),
    name: Yup.string()
      .max(255)
      .required()
      .label(I18n.t('leads.importDialog.nameListLabel')),
    method_choice: Yup.string().required(),
    manual_input: Yup.string()
      .when('method_choice', {
        is: 'input',
        then: Yup.string()
          .required()
          .test(
            'emailList',
            I18n.t('leads.importDialog.manualPlaceholder'),
            (list) => {
              if (!list) return false
              const emails = list.includes(',')
                ? list.split(',')
                : list.split('\n')
              return emails.every((email) =>
                Yup.string().email().isValidSync(email),
              )
            },
          ),
      })
      .label(I18n.t('leads.importDialog.manualLabel')),
    import: Yup.mixed()
      .when('method_choice', {
        is: 'upload',
        then: Yup.mixed()
          .required()
          .test(
            'fileContents',
            I18n.t('shared.invalidFile'),
            async function (value) {
              if (!value) return false
              const data = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => {
                  const csvData = reader.result
                  resolve(csvData)
                }
                reader.onerror = reject
                reader.readAsText(value)
              })
              if (!data) return false
              const valid = csvValidator(String(data))
              return valid === true ? true : this.createError(valid)
            },
          ),
      })
      .label(I18n.t('leads.importDialog.uploadLabel')),
  })
  .label('Field')

const ImportLeadDialog: React.FC<
  React.PropsWithChildren<{
    brandList: [{ id: string; name: string; retailerId: string }]
    isOpen: boolean
    handleClose: () => void
    handleSuccess: (id: string) => void
  }>
> = ({
  brandList,
  isOpen,
  handleClose = () => {},
  handleSuccess = (id: string) => {},
}) => {
  const [createLeadImport] = useMutation(CREATE_LEAD_IMPORT)
  const { enqueueSnackbar } = useSnackbar()
  const headers = useSelector((state) => state.auth.headers)
  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <Formik
        initialValues={{
          brand_id: '',
          name: I18n.t('leads.importDialog.nameListPlaceholder', {
            dateTime: new Date().toLocaleString(),
          }),
          manual_input: '',
          method_choice: 'upload',
          import: '',
        }}
        validationSchema={schema}
        onSubmit={async (values) => {
          let success = false
          let successId = ''
          const retailerId = brandList.find((el) => el.id === values.brand_id)
            ?.retailerId
          if (values.method_choice === 'upload' && retailerId) {
            const formData = new FormData()
            formData.append('lead_import[import]', values.import)
            formData.append('lead_import[name]', values.name)
            formData.append('lead_import[brand_id]', values.brand_id)
            formData.append('lead_import[retailer_id]', retailerId)
            formData.append('lead_import[method_choice]', values.method_choice)
            // graphql federation doesn't support file uploads so we have to use REST
            const response = await fetch(
              `${AppConfig.API_BASE}/v2/retailers/${retailerId}/lead_imports`,
              {
                method: 'POST',
                headers,
                body: formData,
              },
            )
            if (response.ok) {
              const data = await response.json()
              success = true
              successId = data?.id
            }
          } else if (retailerId) {
            const { data } = await createLeadImport({
              variables: {
                resource_id: retailerId,
                resource_type: 'retailers',
                leadImport: {
                  brand_id: values.brand_id,
                  retailer_id: retailerId,
                  name:
                    values.name ??
                    I18n.t('leads.importDialog.nameListPlaceholder', {
                      dateTime: new Date().toLocaleString(),
                    }),
                  manual_input: values.manual_input || undefined,
                  method_choice: values.method_choice,
                },
              },
            }).catch((e) => {
              console.error(e)
              return { data: { errors: e } }
            })
            success = data?.createLeadImport?.id && !data?.errors
            successId = data?.createLeadImport?.id || ''
          }
          if (success) {
            enqueueSnackbar(I18n.t('leads.importDialog.uploadingSuccess'), {
              variant: 'success',
            })
            handleSuccess(successId)
          } else {
            enqueueSnackbar(I18n.t('leads.importDialog.uploadingError'), {
              variant: 'error',
            })
          }
          handleClose()
        }}
      >
        {({ errors, values, isValid, isSubmitting }) => (
          <Form>
            <DialogTitle>{I18n.t('leads.importDialog.title')}</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2">
                {I18n.t('leads.importDialog.subtitle')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexWrap: 'nowrap',
                }}
              >
                <Field
                  type="select"
                  component={TextField}
                  label={I18n.t('leads.importDialog.brandListLabel')}
                  placeholder={I18n.t(
                    'leads.importDialog.brandListPlaceholder',
                  )}
                  name="brand_id"
                  select
                  required
                >
                  {brandList.map(
                    (brand) =>
                      brand.id && (
                        <MenuItem key={`brand-${brand.id}}`} value={brand.id}>
                          {brand.name}
                        </MenuItem>
                      ),
                  )}
                </Field>
                <Field
                  name="name"
                  label={I18n.t('leads.importDialog.nameListLabel')}
                  placeholder={I18n.t(
                    'leads.importDialog.nameListPlaceholder',
                    {
                      dateTime: new Date().toLocaleString(),
                    },
                  )}
                  component={TextField}
                />

                <Field name="method_choice" component={RadioGroup}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      {I18n.t('leads.importDialog.addListLabel')}
                    </FormLabel>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <FormControlLabel
                        value="upload"
                        control={<Radio />}
                        label={I18n.t('leads.importDialog.uploadLabel')}
                      />

                      <FormControlLabel
                        value="input"
                        control={<Radio />}
                        label={I18n.t('leads.importDialog.manualLabel')}
                      />
                    </Box>
                  </FormControl>
                </Field>

                {values.method_choice === 'upload' ? (
                  <>
                    <FormHelperText>
                      {I18n.t('leads.importDialog.uploadRequirements')}
                      <Link
                        href="https://support.promoboxx.com/hc/en-us/articles/15363080073371-Why-won-t-my-email-list-upload-"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {I18n.t('leads.importDialog.uploadLink')}
                      </Link>
                    </FormHelperText>
                    <FormControl error={Boolean(errors.import)}>
                      <Field
                        type="file"
                        name="import"
                        label={I18n.t('leads.importDialog.uploadLabel')}
                        component={SimpleFileUpload}
                        accept=".csv"
                        error={Boolean(errors.import)}
                      />
                      <FormHelperText>{errors.import}</FormHelperText>
                    </FormControl>
                  </>
                ) : (
                  <Field
                    name="manual_input"
                    label={I18n.t('leads.importDialog.manualLabel')}
                    helperText={I18n.t('leads.importDialog.manualPlaceholder')}
                    component={TextField}
                    fullWidth
                    multiline
                    minRows="2"
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} variant="outlined">
                {I18n.t('shared.cancel')}
              </Button>
              <Button
                color="primary"
                type="submit"
                variant="contained"
                disabled={!isValid || isSubmitting}
              >
                {I18n.t('shared.save')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default ImportLeadDialog
